type CodecKind = 'string' | 'number' | 'boolean' | 'enum' | 'json';

type CodecMeta<T> = {
    type: CodecKind;
    default: T;
    values?: readonly string[];
    emptyString?: 'default' | 'value';
    attribute?: string;
};

interface CodecFn<T> {
    (el: HTMLElement, attr: string): T;
    readonly meta: CodecMeta<T>;
}

type SchemaToProps<T extends Record<string, CodecFn<unknown>>> = {
    [K in keyof T]: ReturnType<T[K]>;
};

type SchemaPropKey<T extends Record<string, CodecFn<unknown>>> = Extract<keyof SchemaToProps<T>, string>;

type PropState<T extends Record<string, CodecFn<unknown>>> = SchemaToProps<T> & {
    readonly disconnect: () => void;
};

type PropChangeSource = 'attribute' | 'property';

type PropChange<T extends Record<string, CodecFn<unknown>>> = {
    key: SchemaPropKey<T>;
    previous: SchemaToProps<T>[keyof SchemaToProps<T>];
    value: SchemaToProps<T>[keyof SchemaToProps<T>];
    source: PropChangeSource;
};

type DefinePropsOptions<T extends Record<string, CodecFn<unknown>>> = {
    el: HTMLElement;
    schema: T;
    onChange?: (state: PropState<T>, changes: Array<PropChange<T>>) => void;
};

type DecodePropsOptions<T extends Record<string, CodecFn<unknown>>> = {
    el: HTMLElement;
    schema: T;
};

type ReflectSchemaPropOptions<
    T extends Record<string, CodecFn<unknown>>,
    K extends SchemaPropKey<T>,
> = {
    el: HTMLElement;
    schema: T;
    key: K;
    value: SchemaToProps<T>[K];
};

type ReactiveFn = <T>(value: T) => T;

interface StringCodec extends CodecFn<string> {
    trim: StringCodec;
    lower: StringCodec;
    upper: StringCodec;
    kebab: StringCodec;
    camel: StringCodec;
    snake: StringCodec;
    default(val: string): StringCodec;
    emptyAsValue: StringCodec;
    emptyAsDefault: StringCodec;
    maxLength(n: number): StringCodec;
    prefix(p: string): StringCodec;
    suffix(s: string): StringCodec;
}

interface NumberCodec extends CodecFn<number> {
    clamp(min: number, max: number): NumberCodec;
    snap(step: number): NumberCodec;
    bounded(min: number, max: number, step?: number): NumberCodec;
    min(n: number): NumberCodec;
    max(n: number): NumberCodec;
    step(n: number): NumberCodec;
    default(val: number): NumberCodec;
}

interface BoolCodec extends CodecFn<boolean> {
    default(val: boolean): BoolCodec;
}

interface OneOfCodec<T extends string> extends CodecFn<T> {
    default(val: T): OneOfCodec<T>;
}

interface JsonCodec<T> extends CodecFn<T> {
    default(val: T): JsonCodec<T>;
}

interface JsonCodecFactory {
    <T>(fallback: T): JsonCodec<T>;
    object<T extends Record<string, unknown>>(fallback: T): JsonCodec<T>;
    array<T>(fallback: T[]): JsonCodec<T[]>;
}

const toKebab = (str: string): string => str.replace(/([A-Z])/g, '-$1').toLowerCase();
const dataAttributeForProp = (key: string): string => `data-${toKebab(key)}`;
const attributeForSchemaProp = (key: string, codec: CodecFn<unknown>): string => codec.meta.attribute ?? key;
const dataAttributeForSchemaProp = (key: string, codec: CodecFn<unknown>): string => (
    dataAttributeForProp(attributeForSchemaProp(key, codec))
);
const toKebabStr = (str: string): string => str.replace(/([A-Z])/g, '-$1').toLowerCase().replace(/[\s_]+/g, '-');
const toCamelStr = (str: string): string => str.replace(/[-_\s]+([a-z])/g, (_, c: string) => c.toUpperCase());
const toSnakeStr = (str: string): string => str.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/[\s-]+/g, '_');

const readAttr = (el: HTMLElement, key: string): string | undefined => (
    el.getAttribute(dataAttributeForProp(key)) ?? undefined
);
const hasAttr = (el: HTMLElement, camelKey: string): boolean => el.hasAttribute(dataAttributeForProp(camelKey));

const clampNumber = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));
const snapNumber = (value: number, step: number): number => (
    Number.isFinite(step) && step > 0 ? Math.round(value / step) * step : value
);
const boundNumber = (value: number, min: number, max: number, step = 1): number => (
    clampNumber(snapNumber(value, step), min, max)
);

const attachMeta = <TCodec extends CodecFn<unknown>>(
    codec: TCodec,
    meta: CodecMeta<ReturnType<TCodec>>,
): TCodec => {
    Object.defineProperty(codec, 'meta', {
        value: Object.freeze(meta),
        enumerable: false,
    });
    return codec;
};

const attribute = <T>(codec: CodecFn<T>, name: string): CodecFn<T> => attachMeta(
    ((el: HTMLElement): T => codec(el, name)) as unknown as CodecFn<T>,
    {...codec.meta, attribute: name},
);

function makeStringCodec(
    transforms: Array<(s: string) => string>,
    fallback: string,
    emptyString: 'default' | 'value' = 'default',
): StringCodec {
    const codec = ((el: HTMLElement, attr: string): string => {
        const raw = readAttr(el, attr);
        if (raw === undefined || (raw === '' && emptyString === 'default')) return fallback;
        return transforms.reduce((value, fn) => fn(value), raw);
    }) as StringCodec;

    Object.defineProperties(codec, {
        trim: {get: () => makeStringCodec([...transforms, (s) => s.trim()], fallback, emptyString)},
        lower: {get: () => makeStringCodec([...transforms, (s) => s.toLowerCase()], fallback, emptyString)},
        upper: {get: () => makeStringCodec([...transforms, (s) => s.toUpperCase()], fallback, emptyString)},
        kebab: {get: () => makeStringCodec([...transforms, toKebabStr], fallback, emptyString)},
        camel: {get: () => makeStringCodec([...transforms, toCamelStr], fallback, emptyString)},
        snake: {get: () => makeStringCodec([...transforms, toSnakeStr], fallback, emptyString)},
        default: {value: (val: string) => makeStringCodec(transforms, val, emptyString)},
        emptyAsValue: {get: () => makeStringCodec(transforms, fallback, 'value')},
        emptyAsDefault: {get: () => makeStringCodec(transforms, fallback, 'default')},
        maxLength: {value: (n: number) => makeStringCodec([...transforms, (s) => s.slice(0, n)], fallback, emptyString)},
        prefix: {value: (p: string) => makeStringCodec([...transforms, (s) => (s.startsWith(p) ? s : `${p}${s}`)], fallback, emptyString)},
        suffix: {value: (sfx: string) => makeStringCodec([...transforms, (s) => (s.endsWith(sfx) ? s : `${s}${sfx}`)], fallback, emptyString)},
    });

    return attachMeta(codec, {type: 'string', default: fallback, emptyString});
}

function makeNumberCodec(transforms: Array<(n: number) => number>, fallback: number): NumberCodec {
    const codec = ((el: HTMLElement, attr: string): number => {
        const raw = readAttr(el, attr);
        if (raw === undefined || raw === '') return fallback;
        const parsed = Number(raw);
        if (!Number.isFinite(parsed)) return fallback;
        return transforms.reduce((value, fn) => fn(value), parsed);
    }) as NumberCodec;

    Object.defineProperties(codec, {
        clamp: {value: (min: number, max: number) => makeNumberCodec([...transforms, (n) => clampNumber(n, min, max)], fallback)},
        snap: {value: (step: number) => makeNumberCodec([...transforms, (value) => snapNumber(value, step)], fallback)},
        bounded: {value: (min: number, max: number, step = 1) => makeNumberCodec([...transforms, (value) => boundNumber(value, min, max, step)], fallback)},
        min: {value: (n: number) => makeNumberCodec([...transforms, (v) => Math.max(n, v)], fallback)},
        max: {value: (n: number) => makeNumberCodec([...transforms, (v) => Math.min(n, v)], fallback)},
        step: {value: (n: number) => makeNumberCodec([...transforms, (value) => snapNumber(value, n)], fallback)},
        default: {value: (val: number) => makeNumberCodec(transforms, val)},
    });

    return attachMeta(codec, {type: 'number', default: fallback});
}

function makeBoolCodec(fallback: boolean): BoolCodec {
    const codec = ((el: HTMLElement, attr: string): boolean => {
        const raw = readAttr(el, attr);
        if (raw === undefined) return hasAttr(el, attr) || fallback;
        if (raw === '') return true;
        return ['true', '1', 'yes', 'on'].includes(raw.toLowerCase());
    }) as BoolCodec;

    Object.defineProperties(codec, {
        default: {value: (val: boolean) => makeBoolCodec(val)},
    });

    return attachMeta(codec, {type: 'boolean', default: fallback});
}

function makeOneOfCodec<T extends string>(allowed: readonly T[], fallback: T): OneOfCodec<T> {
    const codec = ((el: HTMLElement, attr: string): T => {
        const raw = readAttr(el, attr) as T | undefined;
        return raw !== undefined && allowed.includes(raw) ? raw : fallback;
    }) as OneOfCodec<T>;

    Object.defineProperties(codec, {
        default: {value: (val: T) => makeOneOfCodec(allowed, val)},
    });

    return attachMeta(codec, {type: 'enum', default: fallback, values: allowed});
}

function makeJsonCodec<T>(fallback: T): JsonCodec<T> {
    const codec = ((el: HTMLElement, attr: string): T => {
        const raw = readAttr(el, attr);
        if (raw === undefined || raw === '') return fallback;

        try {
            return JSON.parse(raw) as T;
        } catch {
            return fallback;
        }
    }) as JsonCodec<T>;

    Object.defineProperties(codec, {
        default: {value: (val: T) => makeJsonCodec(val)},
    });

    return attachMeta(codec, {type: 'json', default: fallback});
}

const decodeProps = <T extends Record<string, CodecFn<unknown>>>({
    el,
    schema,
}: DecodePropsOptions<T>): SchemaToProps<T> => (
    Object.fromEntries(
        Object.entries(schema).map(([key, codec]) => [key, codec(el, attributeForSchemaProp(key, codec))]),
    ) as SchemaToProps<T>
);

const reflectSchemaProp = <
    T extends Record<string, CodecFn<unknown>>,
    K extends SchemaPropKey<T>,
>({
    el,
    schema,
    key,
    value,
}: ReflectSchemaPropOptions<T, K>): void => {
    const codec = schema[key] as CodecFn<SchemaToProps<T>[K]>;
    const attributeName = dataAttributeForSchemaProp(key, codec);
    const serialized = codec.meta.type === 'json' ? JSON.stringify(value) : String(value);
    if (el.getAttribute(attributeName) !== serialized) el.setAttribute(attributeName, serialized);
};

const assignFreshValues = <T extends Record<string, CodecFn<unknown>>>(
    state: SchemaToProps<T>,
    fresh: SchemaToProps<T>,
): Array<Omit<PropChange<T>, 'source'>> => {
    const changes: Array<Omit<PropChange<T>, 'source'>> = [];
    Object.keys(fresh).forEach((key) => {
        const typedKey = key as SchemaPropKey<T>;
        if (!Object.is(state[typedKey], fresh[typedKey])) {
            const previous = state[typedKey];
            state[typedKey] = fresh[typedKey];
            changes.push({
                key: typedKey,
                previous,
                value: fresh[typedKey],
            });
        }
    });
    return changes;
};

let reactive: ReactiveFn = (value) => value;

const setPropReactivity = (nextReactive: ReactiveFn): void => {
    reactive = nextReactive;
};

const defineProps = <T extends Record<string, CodecFn<unknown>>>({
    el,
    schema,
    onChange,
}: DefinePropsOptions<T>): PropState<T> => {
    const schemaKeys = new Set(Object.keys(schema));
    const state = reactive(decodeProps({el, schema})) as PropState<T>;
    let props = null as unknown as PropState<T>;
    let assigningFromAttributes = false;

    const notify = (changes: Array<PropChange<T>>): void => {
        if (changes.length > 0) onChange?.(props, changes);
    };

    props = new Proxy(state, {
        set(target, key, value, receiver): boolean {
            if (typeof key !== 'string' || !schemaKeys.has(key)) {
                return Reflect.set(target, key, value, receiver);
            }

            const typedKey = key as SchemaPropKey<T>;
            const previous = target[typedKey];
            if (Object.is(previous, value)) return true;

            const ok = Reflect.set(target, key, value, receiver);
            if (ok && !assigningFromAttributes) {
                notify([{
                    key: typedKey,
                    previous,
                    value: target[typedKey],
                    source: 'property',
                }]);
            }
            return ok;
        },
    }) as PropState<T>;

    const observer = new MutationObserver(() => {
        assigningFromAttributes = true;
        const changes = assignFreshValues(state, decodeProps({el, schema}))
            .map((change) => ({...change, source: 'attribute' as const}));
        assigningFromAttributes = false;
        notify(changes);
    });

    observer.observe(el, {
        attributes: true,
        attributeFilter: Object.entries(schema).map(([key, codec]) => dataAttributeForSchemaProp(key, codec)),
    });

    Object.defineProperty(props, 'disconnect', {
        value: () => observer.disconnect(),
        enumerable: false,
    });

    return props;
};

const string = makeStringCodec([], '');
const number = makeNumberCodec([], 0);
const bool = makeBoolCodec(false);
const json = Object.assign(
    <T>(fallback: T): JsonCodec<T> => makeJsonCodec<T>(fallback),
    {
        object: <T extends Record<string, unknown>>(fallback: T): JsonCodec<T> => makeJsonCodec<T>(fallback),
        array: <T>(fallback: T[]): JsonCodec<T[]> => makeJsonCodec<T[]>(fallback),
    },
) as JsonCodecFactory;
const oneOf = <T extends string>(allowed: readonly T[], fallback: T): OneOfCodec<T> => makeOneOfCodec<T>(allowed, fallback);

export {
    attribute,
    boundNumber,
    clampNumber,
    decodeProps,
    dataAttributeForProp,
    dataAttributeForSchemaProp,
    defineProps,
    reflectSchemaProp,
    setPropReactivity,
    snapNumber,
    string,
    number,
    bool,
    json,
    oneOf,
};
export type {
    BoolCodec,
    CodecFn,
    CodecKind,
    CodecMeta,
    DecodePropsOptions,
    DefinePropsOptions,
    JsonCodec,
    JsonCodecFactory,
    NumberCodec,
    OneOfCodec,
    PropChange,
    ReflectSchemaPropOptions,
    PropChangeSource,
    PropState,
    ReactiveFn,
    SchemaToProps,
    StringCodec,
};
